import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Github, FileText, Linkedin, ArrowUpRight, Sprout, Briefcase } from 'lucide-react';
import { gsap, useRevealOnScroll } from '../../hooks/useGsap';
import { casesData } from '../../data/casesData';

const TeamMember = ({ name, role, photo, linkedin }) => (
  <div className="flex flex-col items-center group">
    <div className="relative mb-2">
      <img
        src={photo}
        alt={name}
        className="w-16 h-16 rounded-full object-cover ring-2 ring-white/10 shadow-md group-hover:ring-primary-400 transition-all duration-300"
        onError={(e) => {
          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=16a34a&color=fff&size=80`;
        }}
      />
      {linkedin && (
        <a
          href={linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute -bottom-1 -right-1 bg-primary-500 p-1.5 rounded-full text-night-950 hover:bg-primary-300 transition-colors shadow-md"
        >
          <Linkedin size={12} />
        </a>
      )}
    </div>
    <p className="font-semibold text-xs text-mist-100 text-center">{name}</p>
    {role && <p className="text-[10px] text-mist-500 text-center mt-0.5">{role}</p>}
  </div>
);

const CaseCard = ({ caseItem, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!bodyRef.current) return;
    if (isExpanded) {
      gsap.fromTo(
        bodyRef.current,
        { height: 0, opacity: 0 },
        { height: 'auto', opacity: 1, duration: 0.55, ease: 'power3.out' }
      );
    }
  }, [isExpanded]);

  return (
    <div data-case-card className="group relative bg-night-800 border border-white/[0.07] rounded-3xl overflow-hidden transition-all duration-300 hover:border-primary-400/40 hover:shadow-[0_30px_60px_-30px_rgba(74,222,128,0.15)]">
      <button
        type="button"
        className="w-full text-left p-7 md:p-9 flex items-start gap-6"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className="hidden md:flex shrink-0 font-mono text-sm text-mist-600 pt-1">
          {String(index + 1).padStart(2, '0')}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="pill">{caseItem.company}</span>
            {caseItem.repository && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-primary-400 text-night-950 border border-primary-400">
                Open Source
              </span>
            )}
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-bold text-mist-50 leading-tight tracking-tight">
            {caseItem.title}
          </h3>
          <p className="mt-4 text-mist-400 leading-relaxed line-clamp-2 md:line-clamp-2">
            {caseItem.description}
          </p>
        </div>
        <span className="shrink-0 self-start mt-1 inline-flex items-center justify-center h-11 w-11 rounded-full bg-white/[0.07] text-mist-50 group-hover:bg-primary-400 group-hover:text-night-950 transition-colors">
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>

      {isExpanded && (
        <div ref={bodyRef} className="px-7 md:px-9 pb-9 overflow-hidden">
          <div className="hairline mb-8" />

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 space-y-6">
              <div>
                <span className="eyebrow">Sobre o projeto</span>
                <p className="mt-4 text-mist-300 leading-relaxed">{caseItem.description}</p>
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                {caseItem.repository && (
                  <a
                    href={caseItem.repository}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/[0.08] text-mist-50 border border-white/10 rounded-full hover:bg-white/[0.15] transition-colors text-sm font-medium"
                  >
                    <Github size={15} />
                    Repositório
                  </a>
                )}
                {caseItem.documentation && (
                  <a
                    href={caseItem.documentation}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-400 text-night-950 rounded-full hover:bg-primary-300 transition-colors text-sm font-medium"
                  >
                    <FileText size={15} />
                    Documentação
                  </a>
                )}
                {caseItem.companyUrl && (
                  <a
                    href={caseItem.companyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-transparent text-primary-300 border border-primary-400/40 rounded-full hover:bg-primary-400/10 transition-colors text-sm font-medium"
                  >
                    <ExternalLink size={15} />
                    Site da Empresa
                  </a>
                )}
                {caseItem.links && caseItem.links.map((link, idx) => (
                  <a
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/[0.05] text-mist-200 border border-white/10 rounded-full hover:bg-white/[0.12] transition-colors text-sm font-medium"
                  >
                    <ArrowUpRight size={15} />
                    {link.label}
                  </a>
                ))}
              </div>
            </div>

            {caseItem.videoUrl ? (
              <div className="lg:col-span-2">
                <span className="eyebrow">Vídeo</span>
                <div className="mt-4 aspect-video rounded-2xl overflow-hidden bg-night-950 shadow-lg ring-1 ring-white/10">
                  <iframe
                    width="100%"
                    height="100%"
                    src={caseItem.videoUrl.replace('youtu.be/', 'www.youtube.com/embed/').replace('watch?v=', 'embed/')}
                    title={caseItem.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              </div>
            ) : null}
          </div>

          <div className="mt-10 space-y-8">
            {caseItem.governance && caseItem.governance.length > 0 && (
              <div>
                <span className="eyebrow">Governança</span>
                <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                  {caseItem.governance.map((member, idx) => (
                    <TeamMember key={idx} {...member} />
                  ))}
                </div>
              </div>
            )}

            {caseItem.developers && caseItem.developers.length > 0 && (
              <div>
                <span className="eyebrow">Desenvolvedores</span>
                <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                  {caseItem.developers.map((member, idx) => (
                    <TeamMember key={idx} {...member} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Cases = () => {
  const [activeTab, setActiveTab] = useState('agritech');
  const ref = useRevealOnScroll();
  const listRef = useRef(null);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const items = list.querySelectorAll('[data-case-card]');
    if (!items.length) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        items,
        { y: 28, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out', stagger: 0.08, overwrite: true }
      );
    }, list);
    return () => ctx.revert();
  }, [activeTab]);

  const tabs = [
    { id: 'agritech', label: 'AgriTech', icon: Sprout, count: casesData.agritech.length },
    { id: 'agribusiness', label: 'AgriBusiness', icon: Briefcase, count: casesData.agribusiness.length },
  ];

  return (
    <section id="cases" ref={ref} className="relative py-28 bg-night-900 overflow-hidden">
      <div className="absolute top-10 right-0 pointer-events-none select-none">
        <span className="font-display text-[16rem] font-black text-stroke opacity-[0.04] leading-none">CASES</span>
      </div>

      <div className="container-custom relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14">
          <div className="lg:col-span-7">
            <span className="eyebrow reveal">Nossos cases entregues</span>
            <h2 className="section-title reveal mt-4">
              Soluções reais para <span className="text-gradient">desafios reais</span> do agro
            </h2>
          </div>
          <div className="lg:col-span-5 reveal">
            <p className="text-mist-400 text-lg leading-relaxed">
              Cada projeto nasce de uma dor concreta do agronegócio. Estruturamos escopo,
              cronograma e entregas - e devolvemos código aberto para a comunidade.
            </p>
          </div>
        </div>

        <div className="reveal flex justify-center mb-12">
          <div className="inline-flex bg-night-800 rounded-full p-1.5 border border-white/[0.08] shadow-sm">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={'relative inline-flex items-center gap-2 px-5 md:px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ' + (active ? 'bg-primary-400 text-night-950 shadow-md' : 'text-mist-300 hover:text-mist-50')}
                >
                  <Icon size={16} />
                  {tab.label}
                  <span className={'ml-1 text-[10px] font-mono ' + (active ? 'text-primary-900' : 'text-mist-500')}>
                    {String(tab.count).padStart(2, '0')}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div ref={listRef} className="space-y-5">
          {casesData[activeTab].map((caseItem, idx) => (
            <CaseCard key={activeTab + '-' + caseItem.id} caseItem={caseItem} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Cases;
